import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import TierSection, { TierVariant } from '../../dashboard/TierSection'
import { AutoCoderFrameworkLibrary } from '../../../hooks/useFrameworkLibrary'
import { groupFrameworkTiers } from '../../../utils/frameworkTiers'

interface CodeFramePickerProps {
  open: boolean
  onClose: () => void
  onSelect: (codeFrame: AutoCoderFrameworkLibrary) => void
  library: AutoCoderFrameworkLibrary[]
  loading: boolean
  title?: string
  excludeNames?: string[]
  /** Splits the library into my frames and my colleagues'. */
  currentUserEmail?: string
  /** Managers may move a frame to or from Company-level. */
  canPromote?: boolean
  onSetVisibility?: (codeFrame: AutoCoderFrameworkLibrary, visibility: 'user' | 'company') => void
}

interface PickerTier {
  variant: TierVariant
  title: string
  emptyLabel: string
  frames: AutoCoderFrameworkLibrary[]
}

// The AutoCoder code frame library, in Data Viz's three tiers and drawn with
// the same TierSection as Data Viz and the ThemeAI picker (Adam, 23 Sep: "the
// same three tiers as data").
const CodeFramePicker: React.FC<CodeFramePickerProps> = ({
  open,
  onClose,
  onSelect,
  library,
  loading,
  title = 'Select Code Frame',
  excludeNames = [],
  currentUserEmail,
  canPromote = false,
  onSetVisibility,
}) => {
  const [search, setSearch] = useState('')
  const [openTiers, setOpenTiers] = useState<Partial<Record<TierVariant, boolean>>>({})

  const query = search.trim().toLowerCase()
  const visible = library.filter((fw) => {
    if (excludeNames.includes(fw.data?.name || fw.name)) return false
    if (!query) return true
    return (fw.name || '').toLowerCase().includes(query)
      || (fw.description || '').toLowerCase().includes(query)
      || (fw.created_by || '').toLowerCase().includes(query)
  })
  const grouped = groupFrameworkTiers(visible, currentUserEmail)
  const tiers: PickerTier[] = [
    { variant: 'company', title: 'Company-level', emptyLabel: 'No company-level code frames yet', frames: grouped.company },
    { variant: 'mine', title: 'My code frames', emptyLabel: "You haven't saved any code frames for this job yet", frames: grouped.mine },
    { variant: 'colleagues', title: "My colleagues' code frames", emptyLabel: 'No colleagues have code frames in this job yet', frames: grouped.colleagues },
  ]

  // As in Data Viz, a tier starts open when it has frames, until toggled.
  const isTierOpen = (tier: PickerTier) => openTiers[tier.variant] ?? tier.frames.length > 0
  const toggleTier = (tier: PickerTier) => setOpenTiers({ ...openTiers, [tier.variant]: !isTierOpen(tier) })

  const handleSelect = (fw: AutoCoderFrameworkLibrary) => {
    onSelect(fw)
    setSearch('')
    onClose()
  }

  const renderRow = (fw: AutoCoderFrameworkLibrary) => {
    const isCompany = fw.visibility === 'company'
    return (
      <ListItem
        key={fw.id}
        disablePadding
        secondaryAction={canPromote && onSetVisibility ? (
          <Tooltip title={isCompany ? 'Demote from company-level' : 'Promote to company-level'}>
            <IconButton
              edge="end"
              size="small"
              onClick={() => onSetVisibility(fw, isCompany ? 'user' : 'company')}
              aria-label={isCompany ? `Demote ${fw.name} from company-level` : `Promote ${fw.name} to company-level`}
            >
              {isCompany ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : undefined}
        sx={{ backgroundColor: 'background.paper', borderRadius: 1, mb: 0.5 }}
      >
        <ListItemButton
          onClick={() => handleSelect(fw)}
          // The library id, not the name: two code frames may legitimately
          // share a display name, and the secondary line interpolates a code
          // COUNT, which changes as the frame is edited.
          data-testid={`autocoder-code-frame-option-${fw.id}`}
          sx={{ borderRadius: 1 }}
        >
          <ListItemText
            primary={fw.name}
            secondary={[
              `${fw.data?.main_variable?.codes?.length || 0} codes`,
              fw.created_by,
              fw.description,
            ].filter(Boolean).join(' · ')}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="autocoder-code-frame-picker">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search code frames..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          inputProps={{ 'data-testid': 'autocoder-code-frame-picker-search' }}
          sx={{ mb: 2, mt: 1 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : library.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No code frames in your library yet.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 480, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {tiers.map((tier) => (
              <TierSection
                key={tier.variant}
                title={tier.title}
                count={tier.frames.length}
                open={isTierOpen(tier)}
                onToggle={() => toggleTier(tier)}
                variant={tier.variant}
              >
                {tier.frames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, px: 1 }}>
                    {query ? 'No matches' : tier.emptyLabel}
                  </Typography>
                ) : (
                  <List disablePadding>{tier.frames.map(renderRow)}</List>
                )}
              </TierSection>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CodeFramePicker
